import { createSlice } from '@reduxjs/toolkit'
import { Position } from 'geojson'
import turf from 'turf'
import { MAX_TRAJECTORY_DISTANCE, MAX_TRAJECTORY_POINTS, MISSION_JOB_STATUSES, PROGRESS_TYPES } from 'utils/constants'
import { IRobotInfo, IRobotState, IRobotTelemetry } from 'utils/interfaces'

export const initialState: IRobotState = {
  loadingProgress: PROGRESS_TYPES.IDLE,
  robotInfo: undefined,
  telemetry: {} as IRobotTelemetry,
  trajectory: {
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: [],
    },
    properties: {},
  },
  jobInfo: {
    creatingProgress: PROGRESS_TYPES.IDLE,
    uploadingProgress: PROGRESS_TYPES.IDLE,
  },
}

export const robotSlice = createSlice({
  name: 'robot',
  initialState: initialState,
  reducers: {
    setLoadingProgress: (state: IRobotState, action: { payload: PROGRESS_TYPES }) => {
      state.loadingProgress = action.payload
    },
    setRobotInfo: (state: IRobotState, action: { payload: IRobotInfo | undefined | null }) => {
      // If payload is undefined or null, clear the robot info
      if (!action.payload) {
        state.robotInfo = undefined
        return
      }

      // If this is the first time, always update
      if (!state.robotInfo) {
        state.robotInfo = action.payload
        return
      }

      const newMissionsJobsIds =
        action.payload.robotRaw?.missions_jobs
          ?.map(t => t.id)
          .sort()
          .join(',') || ''
      const newOdysseysIds =
        action.payload.robotRaw?.odysseys
          ?.map(o => o.id)
          .sort()
          .join(',') || ''
      const prevMissionsJobsIds =
        state.robotInfo?.robotRaw?.missions_jobs
          ?.map(t => t.id)
          .sort()
          .join(',') || ''
      const prevOdysseysIds =
        state.robotInfo?.robotRaw?.odysseys
          ?.map(o => o.id)
          .sort()
          .join(',') || ''

      // Check if task IDs changed
      const taskIdsChanged = newMissionsJobsIds !== prevMissionsJobsIds || newOdysseysIds !== prevOdysseysIds

      // Check if task statuses changed (even if IDs are the same)
      let taskStatusesChanged = false
      if (!taskIdsChanged) {
        // Same task IDs - check if any status changed
        const newMissionsJobsStatuses =
          action.payload.robotRaw?.missions_jobs
            ?.map(t => `${t.id}:${t.status || ''}`)
            .sort()
            .join(',') || ''
        const newOdysseysStatuses =
          action.payload.robotRaw?.odysseys
            ?.map(o => `${o.id}:${o.status || ''}`)
            .sort()
            .join(',') || ''
        const prevMissionsJobsStatuses =
          state.robotInfo?.robotRaw?.missions_jobs
            ?.map(t => `${t.id}:${t.status || ''}`)
            .sort()
            .join(',') || ''
        const prevOdysseysStatuses =
          state.robotInfo?.robotRaw?.odysseys
            ?.map(o => `${o.id}:${o.status || ''}`)
            .sort()
            .join(',') || ''
        taskStatusesChanged =
          newMissionsJobsStatuses !== prevMissionsJobsStatuses || newOdysseysStatuses !== prevOdysseysStatuses
      }

      const tasksChanged = taskIdsChanged || taskStatusesChanged

      // Check if important robot state fields have changed
      const activeModeChanged = action.payload.activeMode !== state.robotInfo.activeMode
      const activeCommandChanged = action.payload.activeCommand !== state.robotInfo.activeCommand
      const networkStatusChanged = action.payload.networkStatus !== state.robotInfo.networkStatus
      const statusChanged = action.payload.status !== state.robotInfo.status
      const jobChanged =
        action.payload.job?.id !== state.robotInfo.job?.id || action.payload.job?.status !== state.robotInfo.job?.status

      // Check if mission sequence number changed (for Odyssey missions)
      const missionSequenceChanged =
        action.payload.robotRaw?.last_mission_sequence_number !== state.robotInfo.robotRaw?.last_mission_sequence_number

      // Check if mission status (progress) has changed by comparing mission_status_last
      // This is important for real-time progress updates
      const currentJobId = action.payload.robotRaw?.last_job_id
      const prevJobId = state.robotInfo.robotRaw?.last_job_id

      let missionStatusChanged = false
      if (currentJobId && prevJobId && currentJobId === prevJobId) {
        // Same job - check if mission_status_last has changed
        const currentJob =
          action.payload.robotRaw?.missions_jobs?.find(job => job.id === currentJobId) ||
          action.payload.robotRaw?.odysseys?.find(job => job.id === currentJobId)
        const prevJob =
          state.robotInfo.robotRaw?.missions_jobs?.find(job => job.id === prevJobId) ||
          state.robotInfo.robotRaw?.odysseys?.find(job => job.id === prevJobId)

        // Compare mission_status_last by serializing to JSON (simple deep comparison)
        const currentStatus = JSON.stringify(currentJob?.mission_status_last)
        const prevStatus = JSON.stringify(prevJob?.mission_status_last)
        missionStatusChanged = currentStatus !== prevStatus
      }

      // Check if lastPosition changed
      const prevLastPosition = state.robotInfo.lastPosition?.coordinates
      const newLastPosition = action.payload.lastPosition?.coordinates
      const lastPositionChanged =
        // Position appeared or disappeared
        (!prevLastPosition && newLastPosition) ||
        (prevLastPosition && !newLastPosition) ||
        // Position coordinates changed significantly
        (prevLastPosition &&
          newLastPosition &&
          (Math.abs(prevLastPosition[0] - newLastPosition[0]) > 0.0001 ||
            Math.abs(prevLastPosition[1] - newLastPosition[1]) > 0.0001))

      // Update if tasks changed, any important robot state field changed, mission sequence changed, mission status changed, or lastPosition changed
      const willUpdate =
        tasksChanged ||
        activeModeChanged ||
        activeCommandChanged ||
        networkStatusChanged ||
        statusChanged ||
        jobChanged ||
        missionSequenceChanged ||
        missionStatusChanged ||
        lastPositionChanged
      if (willUpdate) {
        state.robotInfo = action.payload
      }
    },
    updateRobotLocations: (state: IRobotState, action: { payload: string[] | null | undefined }) => {
      if (state.robotInfo) {
        state.robotInfo.locations = action.payload
      }
    },
    setTelemetry: (state: IRobotState, action: { payload: Partial<IRobotTelemetry> }) => {
      // Backend sends all actual fields in each message (exclude_none=True)
      // If field is in payload → it's actual → set it
      // If field is explicitly set to undefined in payload → it's stale → clear it
      const payload = action.payload as any

      // Update all enumerable properties (those with actual values)
      // telemetry is always initialized as {} in initialState, so it's safe to use Object.assign
      Object.assign(state.telemetry!, payload)

      // Explicitly clear fields that are set to undefined in payload
      // Note: undefined properties are not enumerable, so Object.keys() won't include them
      const optionalFields: Array<keyof IRobotTelemetry> = [
        'robotBattery',
        'joystickBattery',
        'dirtyWaterTankFilling',
        'temperatureControlUnit',
        'temperatureChassis',
        'temperatureLidar',
        'temperatureExternal',
        'trashFullness',
        'localization_info',
        'firmware_info',
        'system_diagnostics',
      ]

      optionalFields.forEach(field => {
        if (Object.prototype.hasOwnProperty.call(payload, field) && payload[field] === undefined) {
          ;(state.telemetry as any)[field] = undefined
        }
      })
    },
    pushPositionToTrajectory: (state: IRobotState, action: { payload: Position }) => {
      const [lastPoint] = state.trajectory.geometry.coordinates.slice(-1)
      if (
        lastPoint &&
        turf.distance(turf.point(lastPoint), turf.point(action.payload), 'meters') < MAX_TRAJECTORY_DISTANCE
      ) {
        return
      }
      if (state.trajectory.geometry.coordinates.length >= MAX_TRAJECTORY_POINTS) {
        state.trajectory.geometry.coordinates.shift()
      }
      state.trajectory.geometry.coordinates.push(action.payload)
    },
    setCreatingJobProgress: (state: IRobotState, action: { payload: PROGRESS_TYPES }) => {
      state.jobInfo.creatingProgress = action.payload
    },
    setUploadingJobProgress: (state: IRobotState, action: { payload: PROGRESS_TYPES }) => {
      state.jobInfo.uploadingProgress = action.payload
    },
    setJobId: (state: IRobotState, action: { payload: number }) => {
      if (state.robotInfo?.job) {
        state.robotInfo.job.id = action.payload
      }
    },
    setJobStatus: (state: IRobotState, action: { payload: MISSION_JOB_STATUSES }) => {
      if (state.robotInfo?.job) {
        state.robotInfo.job.status = action.payload
      }
    },
    resetRobot: () => {
      return initialState
    },
  },
})

export const RobotActions = robotSlice.actions

export default robotSlice.reducer
